import React, { useCallback, useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { CheckBadgeIcon, CloudArrowUpIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import GradientIcon from '../../../../components/GradientIcon';
import { Button } from '../../../../components/ui/button';
import { useDispatch, useSelector } from 'react-redux';
import { changeFileRequest, setErrors, setUploadData, setUploadFile, uploadFiles } from '../../../../store/uploads/actions';
import constants from '../../../../commonUtils/constants';
import Loader from '../../../../layout/Loader';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { validateCSVFile } from '../../../../commonUtils/upload.helper';

const UploadFile = ({navBtnsDisabled, setNavBtnsDisabled}) => {
    const dispatch = useDispatch();
    const { loading, loadingText, errors, uploadData, uploadFile } = useSelector((state) => state.Uploads);
    const [openChangeFileConfirm, setOpenChangeFileConfirm] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('initial');
    const [isChangingFile, setIsChangingFile] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if(uploadStatus === 'change_initiated') {
            setNavBtnsDisabled(true);
            setIsChangingFile(true);
        } else if(uploadStatus === 'change_selected') {
            if(isChangingFile) {
                setIsChangingFile(false);
                setNavBtnsDisabled(true);
            }else {
                setUploadStatus('uploaded');
                setNavBtnsDisabled(false);
            }
        } else if ((uploadData?.id || uploadData?._id) && uploadFile?.unique_name) {
            setUploadStatus('uploaded');
            setNavBtnsDisabled(false);
        } else if (uploadFile && !uploadFile?.unique_name && uploadFile?.name && uploadFile?.name !== "0") {
            setUploadStatus('selected');
            setNavBtnsDisabled(true);
        } else if (!uploadFile) {
            setUploadStatus('initial');
            setNavBtnsDisabled(true);
        }
    }, [isChangingFile, setNavBtnsDisabled, uploadData, uploadFile, uploadStatus]);

    const processFile = useCallback(async (file) => {
        // Clear errors immediately when processing starts
        dispatch(setErrors(constants.upload.errorInitialState));
        
        const errorMessages = {};

        // Basic validations
        if (!file.name.toLowerCase().endsWith('.csv')) {
            errorMessages.type = 'Only ".csv" files are allowed.';
        }

        if (
            file.type !== 'text/csv' &&
            file.type !== 'application/vnd.ms-excel'
        ) {
            errorMessages.type = 'The file must be a CSV.';
        }

        if (file.size > constants.upload.max_file_size) {
            errorMessages.size = 'The file must be smaller than 200MB.';
        }

        if (Object.keys(errorMessages).length > 0) {
            dispatch(setErrors({ file: { ...errorMessages } }));
            return;
        }

        // Enhanced validation using helper function
        try {
            const validationErrors = await validateCSVFile(file);
            if (Object.keys(validationErrors).length > 0) {
                dispatch(setErrors({ file: validationErrors }));
                return;
            }
        } catch (error) {
            dispatch(setErrors({ file: { type: 'Unable to validate file. Please try again.' } }));
            return;
        }

        // If validation passes, continue with Papa Parse processing
        const reader = new FileReader();
        reader.onload = (event) => {
            const rawText = event.target.result;

            Papa.parse(rawText, {
                skipEmptyLines: true,
                delimitersToGuess: [',', ';', '\t', '|'],
                complete: (results) => {
                    Papa.parse(rawText, {
                        skipEmptyLines: 'greedy',
                        delimitersToGuess: [',', ';', '\t', '|'],
                        complete: (greedyResults) => {
                            const finalRowCount = greedyResults.data.length;
                            const colCount = greedyResults.data[0]?.length || 0;
                            const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);

                            if (!greedyResults.data || greedyResults.data.length === 0) {
                                dispatch(setErrors({ file: { type: 'CSV file appears to be empty.' } }));
                                return;
                            }

                            if (!greedyResults.data[0] || greedyResults.data[0].length < 2) {
                                dispatch(setErrors({ file: { type: 'CSV must have at least 2 columns.' } }));
                                return;
                            }

                            // Clear any existing errors before proceeding with successful file processing
                            dispatch(setErrors(constants.upload.errorInitialState));
                            
                            dispatch(setUploadFile(file));
                            dispatch(setUploadData({
                                ...uploadData,
                                fileRowCount: finalRowCount,
                                fileColCount: colCount,
                                fileSizeInMB
                            }));

                            if (isChangingFile) {
                                setUploadStatus('change_selected');
                            } else {
                                setUploadStatus('selected');
                            }
                        }
                    });
                }
            });
        };

        reader.onerror = () => {
            dispatch(setErrors({ file: { type: 'Unable to read file content.' } }));
        };

        reader.readAsText(file);
    }, [dispatch, uploadData, isChangingFile]);

    const onDrop = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        await processFile(acceptedFiles[0]);
    }, [processFile]);

    const onDropRejected = useCallback((fileRejections) => {
        if (fileRejections.length === 0) return;
        
        // Clear existing errors first
        dispatch(setErrors(constants.upload.errorInitialState));
        
        const errorMessages = {};
        const rejection = fileRejections[0];

        rejection.errors.forEach(error => {
            if (error.code === 'file-invalid-type') errorMessages.type = 'Only ".csv" files are allowed.';
            if (error.code === 'file-too-large') errorMessages.size = 'The file must be smaller than 200MB.';
            if (error.code === 'too-many-files') errorMessages.count = 'Only one file can be uploaded.';
        });

        dispatch(setErrors({ file: { ...errorMessages } }));
    }, [dispatch]);

    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'text/csv': ['.csv'] },
        onDrop,
        onDropRejected,
        maxFiles: 1,
        maxSize: constants.upload.max_file_size,
        multiple: false,
        disabled: uploadStatus !== 'initial',
        noClick: false
    });

    const handleClickNoAction = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleUploadFile = (e) => {
        handleClickNoAction(e);
        if (uploadStatus === 'change_selected') {
            dispatch(changeFileRequest({
                id: uploadData.id || uploadData._id,
                uploadData: { 
                    sourceType: uploadData.sourceType,
                    masterProject: uploadData.masterProject,
                    project: uploadData.project,
                    vendor: uploadData.vendor,
                    channelPartner: uploadData.channelPartner,
                    spoc: uploadData.spoc,
                    uploadedBy: uploadData.uploadedBy,
                    dataCategory: uploadData.dataCategory,
                    leadSource: uploadData.leadSource,
                    extractedOn: uploadData.extractedOn,
                    procReason: uploadData.procReason,
                    fileRowCount: uploadData.fileRowCount,
                    fileColCount: uploadData.fileColCount,
                    fileSizeInMB: uploadData.fileSizeInMB,
                    columnMapping: {},
                },
                uploadFile
            }));
        } else {
            dispatch(uploadFiles({ uploadData, uploadFile }));
        }
    };

    const confirmChangeFile = (e) => {
        handleClickNoAction(e);
        setOpenChangeFileConfirm(true);
    };

    // const handleReset = (e) => {
    //     handleClickNoAction(e);
    //     // Clear errors when resetting
    //     dispatch(setErrors(constants.upload.errorInitialState));
    //     dispatch(setUploadFile(null));
    //     setUploadStatus('initial');
    // };

    const handleCancelChange = (e) => {
        handleClickNoAction(e);
        // Clear errors when canceling change
        dispatch(setErrors(constants.upload.errorInitialState));
        setUploadStatus('uploaded');
    };

    const confirmAction = (e) => {
        handleClickNoAction(e);
        setOpenChangeFileConfirm(false);
        setUploadStatus('change_initiated');
        // Clear errors when initiating change
        dispatch(setErrors(constants.upload.errorInitialState));
        setTimeout(() => {
            if (fileInputRef.current) fileInputRef.current.click();
        }, 10);
    };

    const handleFileInputChange = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.target.files[0];
        if (file) {
            await processFile(file);
        }
        e.target.value = '';
    };

    const handleClose = (e) => {
        handleClickNoAction(e);
        setOpenChangeFileConfirm(false);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className='flex flex-col gap-4'>
                    <div><Loader /></div>
                    <p>{loadingText}</p>
                </div>
            );
        }

        switch (uploadStatus) {
            case 'initial':
                return (
                    <div className="p-0 flex flex-col items-center justify-center">
                        <GradientIcon Icon={CloudArrowUpIcon} className="h-14 w-14 md:h-20 md:w-20" />
                        <p className="text-gray-800 mb-2">Drag files to upload</p>
                        <p className="text-gray-800 mb-2">OR</p>
                        <Button className="mb-3">Browse files</Button>
                        <p className="text-gray-500 mb-2">Only ".csv" files are allowed</p>
                        {errors?.file?.type && <small className="text-destructive text-xs mt-1">{errors.file.type}</small>}
                        {errors?.file?.size && <small className="text-destructive text-xs mt-1">{errors.file.size}</small>}
                        {errors?.file?.count && <small className="text-destructive text-xs mt-1">{errors.file.count}</small>}
                    </div>
                );

            case 'selected':
            case 'change_selected':
                return (
                    <div className="p-0 flex flex-col items-center justify-center md:max-w-[400px] text-center">
                        <GradientIcon Icon={DocumentTextIcon} className="h-14 w-14 md:h-20 md:w-20" />
                        <p className="text-gray-800 mb-2"><span className='font-bold'>File Name:</span> {uploadFile?.name || uploadFile?.original_name || ""}</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>File Size:</span> {uploadData?.fileSizeInMB} MB</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>Rows:</span> {uploadData?.fileRowCount?.toLocaleString()}</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>Columns:</span> {uploadData?.fileColCount}</p>
                        <div className="flex mb-3">
                            <Button className="ml-2" onClick={handleUploadFile}>Upload</Button>
                        </div>
                        {/* Show errors in selected state as well */}
                        {errors?.file?.type && <small className="text-destructive text-xs mt-1">{errors.file.type}</small>}
                        {errors?.file?.size && <small className="text-destructive text-xs mt-1">{errors.file.size}</small>}
                        {errors?.file?.count && <small className="text-destructive text-xs mt-1">{errors.file.count}</small>}
                    </div>
                );

            case 'uploaded':
            case 'change_initiated':
                return (
                    <div className="p-0 flex flex-col items-center justify-center md:max-w-[400px] text-center">
                        <GradientIcon Icon={CheckBadgeIcon} className="h-14 w-14 md:h-20 md:w-20" />
                        <p className="text-gray-800 mb-2">{uploadStatus === 'change_initiated' ? "File Change in Progress..." : "File Uploaded Successfully"}</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>File Name:</span> {uploadFile?.name || uploadFile?.original_name || ""}</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>File Size:</span> {uploadData?.fileSizeInMB} MB</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>Rows:</span> {uploadData?.fileRowCount?.toLocaleString()}</p>
                        <p className="text-gray-800 mb-2"><span className='font-bold'>Columns:</span> {uploadData?.fileColCount}</p>
                        <div className="flex mb-3">
                            {uploadStatus==="change_initiated" && <Button variant="outline" onClick={handleCancelChange}>Cancel</Button>}
                            <Button className="ml-2" onClick={confirmChangeFile}>Change File</Button>
                        </div>
                        {uploadStatus === 'change_initiated' && (
                            <>
                                {errors?.file?.type && <small className="text-destructive text-xs mt-1">{errors.file.type}</small>}
                                {errors?.file?.size && <small className="text-destructive text-xs mt-1">{errors.file.size}</small>}
                                {errors?.file?.count && <small className="text-destructive text-xs mt-1">{errors.file.count}</small>}
                            </>
                        )}
                        <ConfirmDialog
                            open={openChangeFileConfirm}
                            titleText="Are you sure you want to change this file?"
                            descriptionText="This action cannot be undone."
                            actionText="Confirm"
                            handleClose={handleClose}
                            confirmAction={(e) => confirmAction(e)}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <div {...getRootProps()} className="p-4 md:px-6 md:py-6 cursor-pointer">
                <input {...getInputProps()} />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                />
                {renderContent()}
            </div>
        </>
    );
};

export default UploadFile;